import { Box, Flex, FormControl, SimpleGrid, Stack, Text, useMediaQuery } from '@chakra-ui/react';
import { conversationAction } from 'actions';
import ModelSelector from 'components/ModelSelector';
import Textarea from 'components/Textarea';
import { useFormik } from 'formik';
import { SET_TIMEOUT_STREAMING_ANSWER } from 'helpers/constant';
import { CONVERSATION_STATUS, VIEW_MODE } from 'helpers/enum';
import { getUserInfoStorage, setLastSelectedModel } from 'helpers/lib';
import { ArrowUp } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ConversationService } from 'services/conversation/conversation.service';
import { Model } from 'services/model/model.type';
import { Citation } from 'types/reducers/conversation';

import Message from './Message';
import { pickRandomCards } from './suggestedQuestions';

interface ChatProps {
  onOpenFile: (_citation: Citation) => void;
  onAddNew: () => void;
}

const Chat = ({ onOpenFile, onAddNew }: ChatProps) => {
  const { t } = useTranslation();
  const chatRef = useRef<HTMLDivElement>(null);
  const cancelEditRef = useRef<HTMLButtonElement>(null);
  const userInfo = getUserInfoStorage();
  const [isMobile] = useMediaQuery('(max-width: 30em)');
  const [isTablet] = useMediaQuery('(max-width: 48em)');
  let timer: ReturnType<typeof setTimeout> | any;

  const dispatch = useDispatch();
  const { status, current, viewMode, isLimitReached, processingStreamingChunkStatus } = useSelector(
    (state) => state.conversations
  );

  const models = useSelector((state: any) => state.model?.models || []);

  // Random 4-of-N suggestion cards, stable for this mount (re-rolls on next visit). (#chat-random-suggestions)
  const suggestionCards = useMemo(() => pickRandomCards(4), []);

  const handleModelChange = (model: Model | null) => {
    if (model) {
      setLastSelectedModel(model.id);
      dispatch(conversationAction.updateSelectedModelId(model.id));
    }
  };

  const isChatViewMode = viewMode === VIEW_MODE.CHAT;
  const isGenerateAnswer = status === CONVERSATION_STATUS.GENERATE_ANSWER;
  const isDisableQuestionBox =
    !current?.conversation_id ||
    isGenerateAnswer ||
    isLimitReached ||
    Boolean(current.answerChunks.chunk);

  // Composer stays usable on the greeting (no conversation yet): typing + model select + Enter/Send
  // are allowed; submitting there creates a conversation and sends, like a suggestion. #77529r2-greeting
  const isComposerBusy = isGenerateAnswer || isLimitReached || Boolean(current.answerChunks.chunk);

  // AO-02: holds a suggested question while a new conversation is being created,
  // then submitted via the existing send path once `conversation_id` is ready.
  const pendingQuestionRef = useRef<string | null>(null);

  // First name for the greeting; fall back gracefully when unavailable.
  const firstName =
    userInfo?.tokenParsed?.given_name?.trim() ||
    userInfo?.fullName?.trim().split(' ')[0] ||
    userInfo?.name?.trim().split(' ')[0] ||
    '';

  const { values, setFieldValue, handleChange, handleSubmit, resetForm } = useFormik({
    initialValues: {
      question: '',
    },
    onSubmit: (values) => {
      if (values.question && values.question?.trim()) {
        if (current?.conversation_id) {
          // Get selected model info
          const selectedModel = models.find((m: Model) => m.id === current.selectedModelId);
          dispatch(
            conversationAction.addNewQuestion({
              ...values,
              created_at: new Date().toISOString(),
              model: selectedModel
                ? {
                    id: selectedModel.id,
                    name: selectedModel.name,
                  }
                : undefined,
            })
          );
          dispatch(conversationAction.updateStatus(CONVERSATION_STATUS.GENERATE_ANSWER));
          dispatch(conversationAction.updateProcessConversationId(current.conversation_id));
          dispatch(conversationAction.updateProcessingStreamingChunkStatus(true));
          dispatch(conversationAction.setTimeOutForFinalAnswer(true));
          resetForm();
        } else {
          // Greeting (no conversation yet): stage the typed question and create a conversation;
          // the conversation_id effect flushes it through this same submit path. #77529r2-greeting
          pendingQuestionRef.current = values.question;
          onAddNew();
        }
      }
      cancelEditRef?.current?.click();
    },
  });

  const isDisableSubmit = !values.question || isGenerateAnswer || isLimitReached;

  const onChangeMessage = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch(conversationAction.updateViewMode(VIEW_MODE.CHAT));
    handleChange(e);
  };

  // Submit a staged suggested question through the SAME formik send path. Uses setTimeout(0)
  // (not setFieldValue(...).then) because formik's setFieldValue doesn't reliably return a
  // promise — the old `.then(handleSubmit)` could throw and silently swallow the click. #77529r2
  const flushPendingQuestion = () => {
    const question = pendingQuestionRef.current;
    if (!question) return;
    pendingQuestionRef.current = null;
    setFieldValue('question', question);
    setTimeout(() => handleSubmit(), 0);
  };

  // Clicking a suggestion FILLS the composer so the user can review/edit before sending —
  // it does NOT auto-send. Pressing send then goes through the normal submit path. (#suggestion-fill)
  const onSelectSuggestion = (question: string) => {
    if (isGenerateAnswer || isLimitReached) return;
    setFieldValue('question', question);
  };

  // Once a conversation exists, flush any pending suggested question.
  useEffect(() => {
    if (current?.conversation_id && pendingQuestionRef.current) {
      flushPendingQuestion();
    }
  }, [current?.conversation_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (current.setTimeoutFinalAnswer && current.conversation_id) {
      timer = setTimeout(async () => {
        const response = await ConversationService.getLatestMessageConversation({
          conversation_id: current.conversation_id!,
        });

        if (response) {
          dispatch(
            conversationAction.updateNewAnswer({
              conversation_id: response.conversation_id,
              answer: response.answer,
              citations: response.citations,
              requested_model: response.requested_model,
              timestamp: response.updated_at,
              status: 'IDLE',
            })
          );
          dispatch(conversationAction.setTimeOutForFinalAnswer(false));
          dispatch(conversationAction.updateProcessingStreamingChunkStatus(false));
          dispatch(conversationAction.clearStreamingAnswerChunks());
        } else {
          dispatch(conversationAction.setTimeOutForFinalAnswer(false));
          dispatch(conversationAction.updateProcessingStreamingChunkStatus(false));
          dispatch(conversationAction.clearStreamingAnswerChunks());
        }
      }, SET_TIMEOUT_STREAMING_ANSWER);
    }

    return () => clearTimeout(timer);
  }, [current.setTimeoutFinalAnswer, current.conversation_id]);

  useEffect(() => {
    const scrollToBottom = () => {
      const chatElement = document.getElementById('message-container');
      if (chatElement) {
        chatElement.scrollTo({
          top: chatElement.scrollHeight,
          behavior: 'smooth',
        });
      }
    };

    // Defer so the scroll runs AFTER the latest answer (markdown / citations) has laid out;
    // a synchronous scroll fires before the final height settles, leaving the last message
    // hidden below the composer. rAF + a short fallback covers late-growing content. (#78224)
    scrollToBottom();
    const raf = requestAnimationFrame(scrollToBottom);
    const timer = setTimeout(scrollToBottom, 250);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [current, status, current.answerChunks.chunk]);

  return (
    <Flex
      direction='column'
      flex={1}
      overflow='hidden'
      w={isChatViewMode ? '100%' : ''}
      maxW={{ md: `430px`, lg: `600px`, xl: `800px`, '2xl': `1100px` }}
      h='100%'
      bg='bg.canvas'
    >
      {/* Greeting + suggestions show whenever the active chat has no messages yet — not only
          before any conversation exists — so they reappear on EVERY New chat. #77529-12 */}
      {(!current?.conversation_id || current?.data?.length === 0) &&
      !isGenerateAnswer &&
      !current?.answerChunks?.chunk ? (
        <Flex
          align='center'
          justify='center'
          direction='column'
          flex={1}
          gap={6}
          overflowY='auto'
          px={4}
        >
          {/* Empty-state / greeting — matches mockup-v2 (no avatar tile) */}
          <Flex align='center' direction='column' gap={4} textAlign='center'>
            <Text
              color='text.default'
              fontFamily='heading'
              fontSize='2xl'
              fontWeight={600}
              letterSpacing='-0.02em'
            >
              {firstName
                ? t('chat.greeting', { name: firstName, defaultValue: 'How can I help, {{name}}?' })
                : t('chat.greetingNoName', { defaultValue: 'How can I help?' })}
            </Text>
            <Text maxW='460px' color='text.muted' fontSize='base'>
              {t('chat.greetingSubtitle', {
                defaultValue:
                  "Ask anything about your organization's documents, policies and contracts.",
              })}
            </Text>
          </Flex>

          {/* AO-02 Suggestion cards — fixed 2×2 grid, bold title + grey subtitle.
              Sends via the existing formik path (see onSelectSuggestion). */}
          <Box w='100%' maxW='720px'>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              {suggestionCards.map((card) => {
                const title = t(card.titleKey, { defaultValue: card.titleDefault });
                const subtitle = t(card.subtitleKey, { defaultValue: card.subtitleDefault });
                const prompt = t(card.promptKey, { defaultValue: card.promptDefault });
                const isSuggestionDisabled = isGenerateAnswer || isLimitReached;
                return (
                  <Box
                    key={card.titleKey}
                    as='button'
                    px={4}
                    py={4}
                    textAlign='center'
                    bg='bg.surface'
                    opacity={isSuggestionDisabled ? 0.5 : 1}
                    borderWidth='1px'
                    borderColor='border.default'
                    borderRadius='lg'
                    shadow='ds_sm'
                    _hover={
                      isSuggestionDisabled
                        ? undefined
                        : { borderColor: 'border.strong', boxShadow: 'ds_md' }
                    }
                    _active={isSuggestionDisabled ? undefined : { transform: 'translateY(1px)' }}
                    cursor={isSuggestionDisabled ? 'not-allowed' : 'pointer'}
                    transition='border-color .16s ease-out, box-shadow .16s ease-out, transform .16s ease-out'
                    disabled={isSuggestionDisabled}
                    onClick={() => onSelectSuggestion(prompt)}
                    type='button'
                  >
                    <Text color='text.default' fontSize='sm' fontWeight={600}>
                      {title}
                    </Text>
                    <Text mt={1} color='text.subtle' fontSize='xs'>
                      {subtitle}
                    </Text>
                  </Box>
                );
              })}
            </SimpleGrid>
          </Box>
        </Flex>
      ) : (
        <Box
          ref={chatRef}
          sx={{
            '&::-webkit-scrollbar': {
              display: isMobile ? 'none' : 'block',
            },
          }}
          flex={'1 1 0'}
          alignSelf={'stretch'}
          overflowY='auto'
          h='100%'
          minH={0}
          id='message-container'
        >
          <Box maxW={{ base: '100%', lg: '820px' }} m='auto' px={{ base: 4, md: 6 }} pb={4}>
            <Stack direction='column' pt={0} spacing={{ base: 6, lg: '2rem' }}>
              {current?.data?.map((message, index: number) => (
                <Message
                  isLast={index === current.data.length - 1}
                  key={index}
                  data={message}
                  status={status}
                  onOpenFile={onOpenFile}
                  user={userInfo!}
                  cancelEditRef={cancelEditRef}
                />
              ))}
            </Stack>
          </Box>
        </Box>
      )}

      {/* DS composer — surface card, ds_md shadow, focus ring (design-guidelines.md §4) */}
      <Box
        pos='relative'
        zIndex={1000}
        flexShrink={0}
        px={{ base: 4, md: 8 }}
        pt={3}
        pb={{ base: 10, md: 6 }}
      >
        <form onSubmit={handleSubmit}>
          <FormControl>
            <Flex
              direction='column'
              w='100%'
              maxW={{ lg: '820px' }}
              m='auto'
              px={3}
              pt={2}
              pb={2}
              bg='bg.surface'
              borderWidth='1px'
              borderColor='border.strong'
              borderRadius='xl'
              shadow='ds_md'
              _focusWithin={{
                borderColor: 'primary.solid',
                boxShadow: '0 0 0 3px var(--chakra-colors-primary-soft)',
              }}
              transition='border-color .16s ease-out, box-shadow .16s ease-out'
            >
              <Textarea
                name='question'
                variant='unstyled'
                placeholder={t('chat.composerPlaceholder', {
                  defaultValue: 'Ask a question about your documents…',
                })}
                rows={1}
                minH='44px'
                h='44px'
                maxH={isTablet ? '120px' : '180px'}
                border='none'
                borderRadius={0}
                bg='transparent'
                boxShadow='none'
                color='text.default'
                resize='none'
                _placeholder={{ color: 'text.subtle' }}
                _hover={{ bg: 'transparent' }}
                _focus={{ boxShadow: 'none', border: 'none', bg: 'transparent' }}
                _focusVisible={{ boxShadow: 'none', border: 'none', bg: 'transparent' }}
                _disabled={{ bg: 'transparent', opacity: 0.6, cursor: 'not-allowed' }}
                px={2}
                py={2}
                fontSize='base'
                maxExpandHeight={isTablet ? 120 : 180}
                isDisabled={isComposerBusy}
                value={values.question}
                onChange={onChangeMessage}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }
                }}
              />
              <Flex align='center' gap={2} mt={1}>
                {current && (
                  <ModelSelector
                    selectedModelId={current.selectedModelId}
                    onModelChange={handleModelChange}
                    isDisabled={isComposerBusy}
                    menuPlacement='top'
                    width='100%'
                    maxWidth='200px'
                  />
                )}
                <Flex
                  as='button'
                  align='center'
                  justify='center'
                  flexShrink={0}
                  w='38px'
                  h='38px'
                  ml='auto'
                  color='primary.on'
                  bg='primary.solid'
                  opacity={isDisableSubmit ? 0.4 : 1}
                  borderRadius='full'
                  _hover={{ bg: 'primary.hover' }}
                  cursor={isDisableSubmit ? 'not-allowed' : 'pointer'}
                  transition='background .16s ease-out, opacity .16s ease-out'
                  aria-label={t('chat.composerPlaceholder', {
                    defaultValue: 'Ask a question about your documents…',
                  })}
                  onClick={() => !isDisableSubmit && handleSubmit()}
                  type='button'
                >
                  <ArrowUp size={18} />
                </Flex>
              </Flex>
            </Flex>
          </FormControl>
        </form>

        {/* Disclaimer — matches Round-2 mockup */}
        <Text
          w='100%'
          maxW={{ lg: '820px' }}
          m='auto'
          mt={2}
          color='text.subtle'
          fontSize='xs'
          textAlign='center'
        >
          {t('chat.disclaimer', {
            defaultValue:
              'Amatrium can make mistakes. Verify important information against the source documents.',
          })}
        </Text>
      </Box>
    </Flex>
  );
};

export default Chat;
